import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import api from '../api/client'
import {
  readStoredTokenIfUnexpired,
  isAccessTokenUnexpired,
  userFromAccessToken,
  SAT_TOKEN_KEY,
} from '../utils/accessToken'

export interface User {
  id: number
  email: string
  user_name: string
  role: string
  is_active: boolean
  subscription_status: string
  plan_code: string
  /** ISO datetime; null means no fixed end (e.g. lifetime). */
  subscription_end_date?: string | null
  xp_total: number
  avatar?: string
  onboarding_completed: boolean
  target_score_rw?: number | null
  target_score_math?: number | null
  sat_date?: string | null
}

interface AuthContextType {
  user: User | null
  token: string | null
  login: (identifier: string, password: string) => Promise<User>
  register: (data: any) => Promise<void>
  refreshUser: () => Promise<void>
  logout: () => Promise<void>
  isLoading: boolean
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(() => readStoredTokenIfUnexpired())
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!token) {
      setIsLoading(false)
      return
    }

    let cancelled = false

    api
      .get('/auth/me')
      .then((res) => {
        if (!cancelled) setUser(res.data)
      })
      .catch((err) => {
        const status = err.response?.status as number | undefined
        const stored = localStorage.getItem(SAT_TOKEN_KEY)

        if (status === 401 || status === 403) {
          if (stored) localStorage.removeItem(SAT_TOKEN_KEY)
          if (!cancelled) {
            setToken(null)
            setUser(null)
          }
          return
        }

        // Network / 5xx / 404: keep a non-expired JWT; hydrate from claims so "Log in" is not shown.
        if (stored && isAccessTokenUnexpired(stored)) {
          const fromJwt = userFromAccessToken(stored)
          if (fromJwt && !cancelled) setUser(fromJwt)
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [token])

  const login = async (identifier: string, password: string) => {
    const res = await api.post('/auth/login', { email: identifier.trim(), password })
    const { access_token, user: userData } = res.data
    localStorage.setItem(SAT_TOKEN_KEY, access_token)
    setToken(access_token)
    setUser(userData)
    return userData as User
  }

  const register = async (data: any) => {
    await api.post('/auth/register', data)
  }

  const refreshUser = async () => {
    const res = await api.get<User>('/auth/me')
    setUser(res.data)
  }

  const logout = async () => {
    // IMPORTANT: await the server call BEFORE clearing local state.
    //
    // If we clear token/user first, ProtectedRoute immediately unmounts the
    // tree and redirects to /login, which aborts the in-flight POST. The
    // backend then never receives it, so the tbl_user_session row stays
    // active and the next login on another browser gets a 409
    // "You are already signed on". We cap the wait so a slow/offline server
    // still lets the user sign out locally.
    try {
      await api.post('/auth/logout', null, { timeout: 5000 })
    } catch {
      // Network / server errors are non-fatal for logout UX: we still wipe
      // local state below so the user ends up on the login screen.
    }
    localStorage.removeItem(SAT_TOKEN_KEY)
    setToken(null)
    setUser(null)
  }

  return (
    <AuthContext.Provider value={{ user, token, login, register, refreshUser, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}
